/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DatePickerWrap } from 'components/inputs/DatePicker';
import Datepicker from 'react-tailwindcss-datepicker';
import { Button } from '@mui/material';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { ElmEmpPopup } from 'pages/common/ElmEmpPopup';
import { IconButton } from 'components/buttons/IconSVG';
import { ExcelDownloadRequest } from 'models/common/Excel';
import { getExcelFileName } from 'utils/ExcelUtil';
import { FileTypeName, ManagementMode } from 'models/common/Common';
import { downloadExcelTemplates } from 'apis/common/Excel';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { SensorCondition, Sensor } from 'models/sensor/Sensor';
import { findSensorList, getSensorListDownload, setSensorFile } from 'apis/sensor/Sensor';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { ComboBox } from 'components/selects/ComboBox';
import { deleteSensorRequest } from 'apis/sensor/popup/SensorReq';
import FileUploadPopUp from 'pages/common/components/FileUploadPopUp';
import { UseToggle } from 'components/inputs/CustomInput';
import CustomGrid from 'components/grids/CustomGrid';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import GridSettingButtons from 'components/grids/GridSettingButtons';

interface SensorPageLocationState {
  snsrMgtNo?: string;
  snsrNm?: string;
  useYn?: string;
  condition?: any;
  pageNo?: number;
}

interface DisplayCondition {
  dataInsUserNm?: string; // 등록자명
}

export const SensorListPage = () => {
  const { t } = useTranslation();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const navigate = useNavigate();
  const gridRef = useRef<any>();
  const gridRef2 = useRef<any>();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);
  const defaultCondition = {
    useYn: 'Y',
  };
  const [sensorCondition, setSensorCondition] = useState<SensorCondition>(defaultCondition);
  const locationState: SensorPageLocationState = useLocation().state;
  const [userData, setUserData] = useState<any>('');
  const [isOpenElmEmpPopup, setOpenElmEmpPopup] = useState<boolean>(false);
  const [empPopupCondition, setEmpPopupCondition] = useState({
    fieldId: '',
    initParam: '',
  });
  const [rowData, setRowData] = useState<Sensor[]>([]);
  const [displayCondition, setDisplayCondition] = useState<DisplayCondition>({});
  const [code, setCode] = useState<any>({});
  const [isOpenFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);
  const [fileUploadModalCondition, setFileUploadModalCondition] = useState<any>({});

  useEffect(() => {
    getCommonCodes();
    searchBtn();
  }, []);

  useEffect(() => {
    getSnsrLv2Code(sensorCondition?.snsrLv1Cd);
  }, [sensorCondition?.snsrLv1Cd]);

  useEffect(() => {
    getSnsrLv3Code(sensorCondition?.snsrLv2Cd);
  }, [sensorCondition?.snsrLv2Cd]);

  const getCommonCodes = async () => {
    const snsrLv1Cd = await getCommonCodeNames('SNSR_LV1_CD');

    setCode({
      snsrLv1Cd: snsrLv1Cd,
    });
  };

  const handleChange = (name: string, value: string) => {
    setSensorCondition({
      ...sensorCondition,
      [name]: value,
    });
  };

  //등록일자 일자(YYYY.MM.DD)까지만 조회
  const dateValueFommater = (params: any) => {
    const date = params.value;
    if (date != null && date != undefined) {
      const subStrDate = date.substr(0, 10);
      const formmattedDate = subStrDate;
      return formmattedDate;
    }
  };

  const handleDisplayConditionChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const name = e.target.name;
    setDisplayCondition({
      ...displayCondition,
      [name]: e.target.value,
    });

    setSensorCondition({
      ...sensorCondition,
      [name.replace('Nm', 'Id')]: '',
    });
  };

  useEffect(() => {
    if (userData != null && userData != '') {
      const fieldId = userData.fieldId;
      const userInfo = userData.userInfo[0] || {};
      if (fieldId) {
        setSensorCondition({
          ...sensorCondition,
          [fieldId.replace('Nm', 'Id')]: userInfo.userId,
        });
        setDisplayCondition({
          ...displayCondition,
          [fieldId]: userInfo.empNm,
        });
      }
    }
  }, [userData]);

  const handleDownloadExcel = () => {
    const snsrClassification = String(t('snsr.label.센서 분류', '센서 분류'));

    const excelData: ExcelDownloadRequest<SensorCondition> = {
      fileName: getExcelFileName(t('snsr.label.센서 조회', '센서 조회')), // + '.xlsx',
      sheetName: t('snsr.label.센서 조회', '센서 조회'),
      header: [
        `${snsrClassification} ${String(t('snsr.grid.센서레벨1', '센서레벨1'))}`,
        `${snsrClassification} ${String(t('snsr.grid.센서레벨2', '센서레벨2'))}`,
        `${snsrClassification} ${String(t('snsr.grid.센서레벨3', '센서레벨3'))}`,
        String(t('snsr.label.센서관리번호', '센서관리번호')),
        String(t('snsr.grid.센서명', '센서명')),
        String(t('snsr.grid.센서 Maker', '센서 Maker')),
        String(t('snsr.grid.IO 링크', 'IO 링크')),
        String(t('snsr.grid.방폭', '방폭')),
        String(t('snsr.grid.IP 등급', 'IP 등급')),
        String(t('snsr.grid.국제인증', '국제인증')),
        String(t('snsr.grid.안전인증', '안전인증')),
        String(t('snsr.grid.Scan/Sample주파수', 'Scan/Sample주파수')),
        String(t('snsr.grid.통신 종류', '통신 종류')),
        String(t('snsr.grid.통신 주기(주파수)', '통신 주기(주파수)')),
        String(t('snsr.grid.구동전원', '구동전원')),
        String(t('snsr.grid.분해능', '분해능')),
        String(t('snsr.grid.응답시간', '응답시간')),
        String(t('snsr.grid.배선사양', '배선사양')),
        String(t('snsr.grid.커넥터사양', '커넥터사양')),
        String(t('snsr.grid.구동온도', '구동온도')),
        String(t('snsr.grid.구동습도', '구동습도')),
        String(t('snsr.grid.감도조절', '감도조절')),
        String(t('snsr.grid.표시등', '표시등')),
        String(t('snsr.grid.관리파라미터1', '관리파라미터1')),
        String(t('snsr.grid.관리파라미터2', '관리파라미터2')),
        String(t('snsr.grid.관리파라미터3', '관리파라미터3')),
        String(t('snsr.grid.관리파라미터4', '관리파라미터4')),
        String(t('snsr.grid.관리파라미터5', '관리파라미터5')),
        // String(t('com.label.첨부', '첨부')),
        String(t('com.label.등록자', '등록자')),
        String(t('com.label.등록일', '등록일자')),
      ],
      searchCondition: sensorCondition,
    };
    getSensorListDownload(excelData);
  };

  const searchBtn = async () => {
    const result = await findSensorList(sensorCondition);
    setRowData(result);
  };

  const getSnsrLv2Code = async (value) => {
    // 센서 레벨3 코드 목록 초기화
    setCode((prev) => ({
      ...prev,
      snsrLv3Cd: [],
    }));

    // 검색조건 초기화
    setSensorCondition((prev) => ({
      ...prev,
      snsrLv2Cd: '',
      snsrLv3Cd: '',
    }));

    if (value) {
      const snsrLv2Cd = await getCommonCodeNamesCondition({
        optValCtn1: value,
        cmnGrCd: 'SNSR_LV2_CD',
      });

      setCode((prev) => ({
        ...prev,
        snsrLv2Cd: snsrLv2Cd,
      }));
    }
  };

  const getSnsrLv3Code = async (value) => {
    // 센서 레벨3 코드값 조기화
    setSensorCondition((prev) => ({
      ...prev,
      snsrLv3Cd: '',
    }));

    if (value) {
      const snsrLv3Cd = await getCommonCodeNamesCondition({
        optValCtn1: value,
        cmnGrCd: 'SNSR_LV3_CD',
      });
      setCode((prev) => ({
        ...prev,
        snsrLv3Cd: snsrLv3Cd,
      }));
    }
  };

  const downloadExcel = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    const id = (selectedRows || [])
      .reduce((acc, cur) => [...acc, cur.dataItem.snsrMgtNo as string], [] as string[])
      .join();
    downloadExcelTemplates(FileTypeName.TPL_SNSR_LIST, id);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchBtn();
    }
  };

  const handleOpenEmpPopup = () => {
    setEmpPopupCondition({
      fieldId: 'dataInsUserNm',
      initParam: displayCondition.dataInsUserNm || '',
    });
    setOpenElmEmpPopup(true);
  };
  const [flexRef, setFlexRef] = useState<any>();
  const onInitialized = (grid) => {
    gridRef.current = grid;
    setFlexRef(gridRef);
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'snsrLv1Nm',
        header: String(t('snsr.grid.Lv1', 'Lv1')),
        align: 'left',
        width: 150,
      },
      {
        binding: 'snsrLv2Nm',
        header: String(t('snsr.grid.Lv2', 'Lv2')),
        align: 'left',
        width: 150,
      },
      {
        binding: 'snsrLv3Nm',
        header: String(t('snsr.grid.Lv3', 'Lv3')),
        align: 'left',
        width: 150,
      },
      {
        binding: 'snsrMgtNo',
        header: String(t('snsr.label.센서관리번호', '센서관리번호')),
        align: 'center',
        width: 130,
      },
      {
        binding: 'snsrNm',
        header: String(t('snsr.label.센서명', '센서명')),
        width: 140,
      },
      {
        binding: 'snsrMkrNm',
        header: String(t('snsr.grid.센서 Maker', '센서Maker')),
        align: 'left',
        allowMerging: true,
        width: 120,
      },
      {
        binding: 'ioLnkYn',
        header: String(t('snsr.grid.IO 링크', 'IO링크')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'expvYn',
        header: String(t('snsr.grid.방폭', '방폭')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'ipGrdCtn',
        header: String(t('snsr.grid.IP 등급', 'IP등급')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'intnCertCtn',
        header: String(t('snsr.grid.국제인증', '국제인증')),
        align: 'center',
        allowMerging: true,
        width: 100,
      },
      {
        binding: 'safeCertCtn',
        header: String(t('snsr.grid.안전인증', '안전인증')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'wvlCtn',
        header: String(t('snsr.grid.Scan/Sample주파수', 'Scan/Sample주파수')),
        align: 'center',
        width: 170,
      },
      {
        binding: 'cmcKndCtn',
        header: String(t('snsr.grid.통신 종류', '통신종류')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'cmcPrdCtn',
        header: String(t('snsr.grid.통신 주기(주파수)', '통신주기(주파수)')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'motnPwrCtn',
        header: String(t('snsr.grid.구동전원', '구동전원')),
        align: 'left',
        width: 110,
      },
      {
        binding: 'rslnCtn',
        header: String(t('snsr.grid.분해능', '분해능')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'rspnTm',
        header: String(t('snsr.grid.응답시간', '응답 시간')),
        align: 'left',
        width: 110,
      },
      {
        binding: 'wireSpecDesc',
        header: String(t('snsr.grid.배선사양', '배선사양')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'cnntSpecDesc',
        header: String(t('snsr.grid.커넥터사양', '커넥터사양')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'motnTmpr',
        header: String(t('snsr.grid.구동온도', '구동온도')),
        align: 'left',
        width: 110,
      },
      {
        binding: 'motnHmd',
        header: String(t('snsr.grid.구동습도', '구동 습도')),
        align: 'left',
        width: 110,
      },
      {
        binding: 'snstChgYn',
        header: String(t('snsr.grid.감도조절', '감도 조절')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'lampYn',
        header: String(t('snsr.grid.표시등', '표시등')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'mgtPram1Ctn',
        header: String(t('snsr.grid.관리파라미터1', '관리파라미터1')),
        align: 'center',
        width: 140,
      },
      {
        binding: 'mgtPram2Ctn',
        header: String(t('snsr.grid.관리파라미터2', '관리파라미터2')),
        align: 'center',
        width: 140,
      },
      {
        binding: 'mgtPram3Ctn',
        header: String(t('snsr.grid.관리파라미터3', '관리파라미터3')),
        align: 'center',
        width: 140,
      },
      {
        binding: 'mgtPram4Ctn',
        header: String(t('snsr.grid.관리파라미터4', '관리파라미터4')),
        align: 'center',
        width: 140,
      },
      {
        binding: 'mgtPram5Ctn',
        header: String(t('snsr.grid.관리파라미터5', '관리파라미터5')),
        align: 'center',
        width: 140,
      },
      {
        binding: 'atchFileCnt', // atchFileCnt
        header: String(t('com.label.첨부', '첨부')),
        align: 'center',
        allowMerging: true,
        width: 70,
        cellTemplate: CellMaker.makeLink({
          text: '${item.atchFileCnt}',
          click: (e, ctx) => {
            setFileUploadModalCondition({
              atchFileGrId: ctx.item.atchFileGrId, //node.data.atchFileGrId,
              atchFileTpCd: 'NORMAL',
              optValCtn1: 'TB_ELM_SNSR_M', // 관련 테이블 명 (확인용)
              bizName: 'snsr', // 파일저장경로 중 업무 폴더명으로 사용 (없는 경우 etc)
              target: ctx,
            });
            setOpenFileUploadModal(true);
          },
        }),
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.등록자', '등록자')),
        align: 'center',
        width: 110,
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.등록일', '등록일')),
        align: 'center',
        width: 140,
        cellTemplate: (params) => dateValueFommater(params),
      },
    ];
  }, [isChangeColumn, flexRef]);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols className="colspan31">
                <label>{t('snsr.label.센서 분류', '센서 분류')}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={code?.snsrLv1Cd}
                  defaultValue={sensorCondition?.snsrLv1Cd}
                  onChange={(value) => handleChange('snsrLv1Cd', value)}
                />
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={code?.snsrLv2Cd}
                  defaultValue={sensorCondition?.snsrLv2Cd}
                  onChange={(value) => handleChange('snsrLv2Cd', value)}
                />
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={code?.snsrLv3Cd}
                  defaultValue={sensorCondition?.snsrLv3Cd}
                  onChange={(value) => handleChange('snsrLv3Cd', value)}
                />
              </SearchCols>
              <SearchCols className="width33">
                <label>{t('snsr.grid.센서 Maker', '센서 Maker')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="snsrMkrNm"
                  placeholder={String(
                    t('snsr.msg.센서 Maker를 입력해 주세요.', '센서 Maker를 입력해 주세요.')
                  )}
                  value={sensorCondition.snsrMkrNm}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols className="width33">
                <label>{t('com.label.등록일', '등록일')}</label>
                <DatePickerWrap className="range">
                  <Datepicker
                    inputClassName="datepickerInput"
                    value={{
                      startDate: sensorCondition?.startDate || null,
                      endDate: sensorCondition?.endDate || null,
                    }}
                    onChange={(value) => {
                      setSensorCondition({
                        ...sensorCondition,
                        startDate: String(value?.startDate || ''),
                        endDate: String(value?.endDate || ''),
                      });
                    }}
                    i18n={'ko'}
                    useRange={true}
                    placeholder="YYYY.MM.DD - YYYY.MM.DD"
                    displayFormat={'YYYY.MM.DD'}
                  />
                </DatePickerWrap>
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{t('snsr.label.센서관리번호', '센서관리번호')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="snsrMgtNo"
                  placeholder={String(
                    t('snsr.msg.센서관리번호를 입력해 주세요.', '센서관리번호를 입력해 주세요.')
                  )}
                  value={sensorCondition.snsrMgtNo}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols className="right">
                <label>{t('snsr.label.센서명', '센서명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="snsrNm"
                  placeholder={String(
                    t('snsr.msg.센서명을 입력해 주세요.', '센서명을 입력해 주세요.')
                  )}
                  value={sensorCondition.snsrNm}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label htmlFor="useYn">{t('com.label.사용여부', '사용여부')}</label>
                <UseToggle className="switch">
                  <input
                    type="checkbox"
                    id="useYn"
                    name="useYn"
                    value={sensorCondition.useYn}
                    checked={sensorCondition.useYn === 'Y'}
                    onChange={() => {
                      setSensorCondition({
                        ...sensorCondition,
                        useYn: sensorCondition.useYn === 'Y' ? 'N' : 'Y',
                      });
                    }}
                  />
                  <span className="slider"></span>
                  <span
                    className="labels"
                    data-on={t('com.label.사용', '사용')}
                    data-off={t('com.label.미사용', '미사용')}
                  ></span>
                </UseToggle>
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.등록자', '등록자')}</label>
                <CustomInputWithSearch
                  name="dataInsUserNm"
                  className="find"
                  placeholder={String(
                    t('com.label.등록자를 선택해 주세요.', '등록자를 선택해 주세요.')
                  )}
                  value={displayCondition.dataInsUserNm}
                  onChange={handleDisplayConditionChange}
                  onSearchClick={handleOpenEmpPopup}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      handleOpenEmpPopup();
                    }
                  }}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            setReset={setSensorCondition}
            defaultCondition={defaultCondition}
            onReset={() => {
              setDisplayCondition(
                (prev) =>
                  Object.keys(prev).reduce(
                    (acc, cur) => Object.assign(acc, { [cur]: '' }),
                    {}
                  ) as DisplayCondition
              );
            }}
            onSearch={searchBtn}
          />
        </SearchBoxRow>
      </SearchBox>

      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('com.label.센서 List', '센서 List')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button css={IconButton.button} className="Exceldown" onClick={downloadExcel}>
            {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={searchBtn} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={'sensorListPageGrid'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => {
              // 노출필드 변경이벤트
              // ref를 이용해 <CustomGrid> 내 함수를 실행
              //openLoading(true);
              gridRef2.current?.changeColumn?.(result);
              setIsChangeColumn(true);
              gridRef.current.refresh();
              //openLoading(false);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>

      <CustomGrid
        id={'sensorListPageGrid'}
        ref={gridRef2}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={570}
        autoCheck={true}
        isReadOnly={true}
        initialized={onInitialized}
      />
      <GlobalBtnGroup>
        <Button
          css={IconButton.button}
          className="write"
          disableRipple
          onClick={(e) => {
            navigate('/sensor/sensor-management-page', {
              replace: true,
              state: {
                upprMnuUrl: location.pathname,
                mnuId: location.pathname,
                mnuNm: String(t('snsr.label.센서 등록/수정', '센서등록')),
                mode: ManagementMode.CREATE_EXCEL,
              },
            });
          }}
        >
          {t('com.button.신규', '신규')}
        </Button>
        <Button
          css={IconButton.button}
          className="write"
          disableRipple
          onClick={(e) => {
            navigate('/sensor/sensor-management-page', {
              replace: true,
              state: {
                upprMnuUrl: location.pathname,
                mnuId: location.pathname,
                mnuNm: String(t('snsr.label.센서 등록/수정', '센서수정')),
                mode: ManagementMode.MODIFY_EXCEL,
              },
            });
          }}
        >
          {t('com.button.수정', '수정')}
        </Button>
        <Button
          css={IconButton.button}
          className="delete"
          onClick={() => {
            const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
            if ((selectedRows || []).length < 1) {
              openMessageBar({
                type: 'error',
                content: t(
                  'com.label.삭제할 대상을 선택해 주세요.',
                  '삭제할 대상을 선택해 주세요.'
                ),
              });
              return;
            }

            openCommonModal({
              modalType: 'confirm',
              content: t(
                'com.label.삭제하시겠습니까? 기존 작업 내용이 사라집니다.',
                '삭제하시겠습니까? 기존 작업 내용이 사라집니다.'
              ),
              yesCallback: () => {
                const rows = selectedRows.map((o) => o.dataItem);
                deleteSensorRequest({ sensorReqList: rows }).then((res) => {
                  if (res.successOrNot === 'Y') {
                    openMessageBar({
                      type: 'confirm',
                      content: t('com.label.삭제되었습니다.', '삭제되었습니다.'),
                    });
                    searchBtn();
                  } else {
                    openMessageBar({
                      type: 'error',
                      content: t(
                        'com.label.삭제 중 오류가 발생했습니다.',
                        '삭제 중 오류가 발생했습니다.'
                      ),
                    });
                  }
                });
              },
            });
          }}
          disableRipple
        >
          {t('com.label.삭제', '삭제')}
        </Button>
      </GlobalBtnGroup>

      {isOpenElmEmpPopup && (
        <ElmEmpPopup
          setUserData={setUserData}
          setPopup={setOpenElmEmpPopup}
          close={() => setOpenElmEmpPopup(false)}
          initParam={empPopupCondition.initParam}
          fieldId={empPopupCondition.fieldId}
        />
      )}

      {isOpenFileUploadModal && (
        <FileUploadPopUp
          open={isOpenFileUploadModal}
          close={() => setOpenFileUploadModal(false)}
          singleSelect={false}
          downloadOnly={false}
          initParam={fileUploadModalCondition}
          onCallback={async (atchFileGrId, fileCount) => {
            if (fileUploadModalCondition.target) {
              await setSensorFile(
                atchFileGrId || '',
                fileUploadModalCondition.target.item.snsrMgtNo || ''
              );
              await searchBtn();
            }
            setOpenFileUploadModal(false);
          }}
          isRequire={true}
        />
      )}
    </>
  );
};
export default SensorListPage;
